import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Star } from "lucide-react";
import { SectionHeader } from "@/components/admin/common";

export default function AdminReviews({ value, onChange, onSave, saving }) {
  const items = value || [];

  const addItem = () => {
    onChange([
      ...items,
      {
        id: `rev-${Date.now()}`,
        name: "Happy Guest",
        meta: "Google review",
        when: "Just now",
        text: "",
        rating: 5,
      },
    ]);
  };

  const update = (idx, patch) =>
    onChange(items.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const remove = (idx) => {
    if (!window.confirm("Remove this review?")) return;
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <SectionHeader
        title="Customer reviews"
        description="Reviews shown in the carousel on the public site. Add what guests love."
        onSave={() => onSave(items)}
        saving={saving}
      />

      <div className="space-y-4">
        {items.map((r, idx) => (
          <div
            key={idx}
            data-testid={`admin-review-${idx}`}
            className="rounded-3xl bg-white border border-[#E8DED5] p-5 md:p-6 space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={r.name}
                  onChange={(e) => update(idx, { name: e.target.value })}
                  className="h-11 rounded-xl mt-1"
                />
              </div>
              <div>
                <Label>Meta (e.g. "Local Guide · 12 reviews")</Label>
                <Input
                  value={r.meta}
                  onChange={(e) => update(idx, { meta: e.target.value })}
                  className="h-11 rounded-xl mt-1"
                />
              </div>
              <div>
                <Label>When</Label>
                <Input
                  value={r.when}
                  onChange={(e) => update(idx, { when: e.target.value })}
                  placeholder="2 weeks ago"
                  className="h-11 rounded-xl mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Review text</Label>
              <Textarea
                value={r.text}
                onChange={(e) => update(idx, { text: e.target.value })}
                rows={3}
                className="rounded-xl mt-1"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => update(idx, { rating: n })}
                    aria-label={`Rate ${n}`}
                    className="p-1"
                  >
                    <Star
                      className={`h-5 w-5 ${
                        n <= (r.rating || 0)
                          ? "fill-[#E53935] text-[#E53935]"
                          : "text-[#E8DED5]"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => remove(idx)}
                className="text-[#E53935] hover:bg-[#E53935]/10"
                data-testid={`admin-delete-review-${idx}`}
              >
                <Trash2 className="h-4 w-4 mr-2" /> Remove
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <Button
          type="button"
          onClick={addItem}
          variant="outline"
          data-testid="admin-add-review"
          className="rounded-full h-11 px-5 border-dashed"
        >
          <Plus className="h-4 w-4 mr-2" /> Add a review
        </Button>
      </div>
    </div>
  );
}
